import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Instagram, ExternalLink } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_5aeb9d65-c68c-4f8e-b371-f49441118154/artifacts/y8ms66px_06.png";

const navLinks = [
  { name: 'Início', path: '/' },
  { name: 'Sobre Nós', path: '/sobre' },
  { name: 'Nossa Jornada', path: '/jornada' },
  { name: 'Equipe', path: '/equipe' },
  { name: 'Conquistas', path: '/conquistas' },
  { name: 'Galeria', path: '/galeria' },
  { name: 'Eventos', path: '/eventos' },
  { name: 'Contato', path: '/contato' },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <nav
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'glass shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img src={LOGO_URL} alt="ACEC Logo" className="h-14 w-auto" />
            <span className={`font-heading font-bold text-xl hidden sm:block ${scrolled ? 'text-blue-700' : 'text-white'}`}>
              ACEC FEMININO
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
                className={`font-medium text-sm uppercase tracking-wide transition-colors relative ${
                  location.pathname === link.path
                    ? scrolled ? 'text-blue-700' : 'text-yellow-400'
                    : scrolled ? 'text-slate-700 hover:text-blue-700' : 'text-white/90 hover:text-white'
                } ${location.pathname === link.path ? 'nav-link-active' : ''}`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <Link
              to="/apoie"
              data-testid="cta-apoie"
              className="bg-yellow-400 text-blue-900 hover:bg-yellow-300 rounded-full px-6 py-2.5 font-bold uppercase text-sm tracking-wider transition-all shadow-lg hover:shadow-yellow-400/30"
            >
              Apoie
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2"
            data-testid="mobile-menu-button"
          >
            {isOpen ? (
              <X className={scrolled ? 'text-slate-900' : 'text-white'} size={28} />
            ) : (
              <Menu className={scrolled ? 'text-slate-900' : 'text-white'} size={28} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-slate-900 overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="px-4 py-6 space-y-4">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link
                    to={link.path}
                    data-testid={`mobile-nav-link-${link.path.replace('/', '') || 'home'}`}
                    className={`block font-heading text-2xl uppercase tracking-wider py-2 ${
                      location.pathname === link.path ? 'text-yellow-400' : 'text-white'
                    }`}
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: navLinks.length * 0.05 }}
                className="pt-4 border-t border-slate-700"
              >
                <Link
                  to="/apoie"
                  data-testid="mobile-cta-apoie"
                  className="block bg-yellow-400 text-blue-900 rounded-full px-6 py-3 font-bold uppercase text-center tracking-wider"
                >
                  Seja um Apoiador
                </Link>
              </motion.div>
              <div className="flex gap-4 pt-4">
                <a
                  href="https://www.instagram.com/acecfeminino"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-yellow-400 transition-colors"
                  data-testid="social-instagram"
                >
                  <Instagram size={24} />
                </a>
                <a
                  href="https://www.tiktok.com/@acec.feminino"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-yellow-400 transition-colors"
                  data-testid="social-tiktok"
                >
                  <ExternalLink size={24} />
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
